import moment from "moment";
import { head, filter, prop, propEq } from "ramda";

class TourPricingService {
  /**
   * Calcula los días efectivos entre dos fechas de manera consistente
   * @param {string} startDate - Fecha de inicio
   * @param {string} endDate - Fecha de fin
   * @returns {number} Número de días efectivos
   */
  static calculateEffectiveDays(startDate, endDate) {
    const start = moment(startDate).utc();
    const end = moment(endDate).utc();
    const days = end.diff(start, "days");
    return days + 1; // Incluir el día de inicio
  }

  /**
   * Calcula el precio base del tour según el modelo de vehículo
   * @param {Object} tourData - Datos del tour
   * @param {string} modelName - Nombre del modelo
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio base
   */
  static calculateBasePrice(tourData, modelName, tourPrices) {
    if (modelName === "Own Motorcycle") {
      const passengerPrice = this.getPriceBySubType(tourPrices, "tour_passenger");
      return passengerPrice + 500; // Cargo adicional para moto propia
    }
    
    const tourTotal = this.getPriceBySubType(tourPrices, "tour_total");
    return tourTotal;
  }

  /**
   * Calcula el precio de GPS con días efectivos corregidos
   * @param {boolean} hasGps - Si tiene GPS habilitado
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {number} effectiveDays - Días efectivos
   * @param {string} modelName - Nombre del modelo
   * @returns {number} Precio de GPS
   */
  static calculateGpsPrice(hasGps, gpsPrices, effectiveDays, modelName) {
    if (!hasGps || modelName === "K1600GT") {
      return 0;
    }

    const gpsPrice = this.getPriceByType(gpsPrices, "gps_device");
    return gpsPrice * effectiveDays;
  }

  /**
   * Calcula el precio del seguro VIP
   * @param {boolean} hasInsurance - Si tiene seguro habilitado
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {number} effectiveDays - Días efectivos
   * @param {string} modelName - Nombre del modelo
   * @returns {number} Precio del seguro VIP
   */
  static calculateVipInsurancePrice(hasInsurance, insurancePrices, effectiveDays, modelName) {
    if (!hasInsurance || modelName === "Own Motorcycle") {
      return 0;
    }

    const insurancePrice = this.getPriceById(insurancePrices, 1); // ID del seguro VIP
    return insurancePrice * effectiveDays;
  }

  /**
   * Calcula el precio del seguro de cancelación
   * @param {boolean} hasCancelInsurance - Si tiene seguro de cancelación
   * @param {Object} tourData - Datos del tour
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Object} options - Opciones seleccionadas
   * @param {number} effectiveDays - Días efectivos
   * @returns {number} Precio del seguro de cancelación
   */
  static calculateCancelInsurancePrice(hasCancelInsurance, tourData, tourPrices, insurancePrices, options, effectiveDays) {
    if (!hasCancelInsurance) {
      return 0;
    }

    // Calcular base para el seguro de cancelación
    const basePrice = this.calculateBasePrice(tourData, options.modelName, tourPrices);
    const gpsPrice = this.calculateGpsPrice(options.gps, tourPrices, effectiveDays, options.modelName);
    const passengerPrice = this.calculatePassengerPrice(tourData, tourPrices);
    const singlePrice = this.calculateSinglePrice(options.single, tourPrices);
    const modelPrice = this.calculateModelPrice(options.modelId, tourPrices);

    const totalBase = basePrice + gpsPrice + passengerPrice + singlePrice + modelPrice;
    
    // Obtener porcentaje del seguro de cancelación
    const cancelInsurancePercentage = this.getPercentageById(insurancePrices, 3); // ID del seguro de cancelación
    
    return (totalBase * cancelInsurancePercentage) / 100;
  }

  /**
   * Calcula el precio del pasajero
   * @param {Object} tourData - Datos del tour
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio del pasajero
   */
  static calculatePassengerPrice(tourData, tourPrices) {
    if (!tourData.booking.p_first_name) {
      return 0;
    }

    return this.getPriceBySubType(tourPrices, "tour_passenger");
  }

  /**
   * Calcula el precio de habitación individual
   * @param {boolean} isSingle - Si es habitación individual
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio de habitación individual
   */
  static calculateSinglePrice(isSingle, tourPrices) {
    if (!isSingle) {
      return 0;
    }

    return this.getPriceBySubType(tourPrices, "tour_single");
  }

  /**
   * Calcula el precio del modelo de vehículo
   * @param {number} modelId - ID del modelo
   * @param {Array} tourPrices - Lista de precios del tour
   * @returns {number} Precio del modelo
   */
  static calculateModelPrice(modelId, tourPrices) {
    // Esta lógica dependerá de cómo se almacenan los precios de modelos
    // Por ahora retornamos 0 como placeholder
    return 0;
  }

  /**
   * Calcula el precio total del tour de manera centralizada
   * @param {Object} tourData - Datos del tour
   * @param {Object} options - Opciones seleccionadas
   * @param {Array} tourPrices - Lista de precios del tour
   * @param {Array} gpsPrices - Lista de precios de GPS
   * @param {Array} insurancePrices - Lista de precios de seguros
   * @param {Object} discounts - Descuentos aplicados
   * @returns {Object} Desglose completo de precios
   */
  static calculateTotalPricing(tourData, options, tourPrices, gpsPrices, insurancePrices, discounts = {}) {
    const effectiveDays = this.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );

    const basePrice = this.calculateBasePrice(tourData, options.modelName, tourPrices);
    const gpsPrice = this.calculateGpsPrice(options.gps, gpsPrices, effectiveDays, options.modelName);
    const vipInsurancePrice = this.calculateVipInsurancePrice(options.insurance, insurancePrices, effectiveDays, options.modelName);
    const passengerPrice = this.calculatePassengerPrice(tourData, tourPrices);
    const singlePrice = this.calculateSinglePrice(options.single, tourPrices);
    const modelPrice = this.calculateModelPrice(options.modelId, tourPrices);
    const cancelInsurancePrice = this.calculateCancelInsurancePrice(
      options.cancelInsurance, 
      tourData, 
      tourPrices, 
      insurancePrices, 
      options, 
      effectiveDays
    );

    const subtotal = basePrice + gpsPrice + vipInsurancePrice + passengerPrice + singlePrice + modelPrice;
    const total = subtotal + cancelInsurancePrice - (discounts.rewards || 0) - (discounts.discount || 0);

    return {
      total,
      subtotal,
      effectiveDays,
      breakdown: {
        basePrice,
        gpsPrice,
        vipInsurancePrice,
        passengerPrice,
        singlePrice,
        modelPrice,
        cancelInsurancePrice,
        effectiveDays
      }
    };
  }

  /**
   * Calcula el valor a cargar considerando depósitos y seguros
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Resultado del cálculo de precios
   * @param {boolean} hasCancelInsurance - Si tiene seguro de cancelación
   * @returns {number} Valor a cargar
   */
  static calculateValueToCharge(tourData, pricing, hasCancelInsurance) {
    const tourStartDate = moment(tourData.booking.tour.tour_start_date);
    const currentDate = moment();
    const daysUntilTour = tourStartDate.diff(currentDate, "days");
    
    let valueToCharge = 0;
    
    // Si el tour está a más de 60 días, cobrar depósito
    if (daysUntilTour >= 60) {
      valueToCharge = 500; // Depósito base
      
      // Si hay pasajero, agregar depósito adicional
      if (tourData.booking.p_first_name) {
        valueToCharge += 500;
      }
      
      // Si tiene seguro de cancelación, agregarlo al depósito
      if (hasCancelInsurance) {
        valueToCharge += pricing.breakdown.cancelInsurancePrice;
      }
    } else {
      // Si el tour está a menos de 60 días, cobrar el total
      valueToCharge = pricing.total;
    }
    
    return valueToCharge;
  }

  /**
   * Valida que los cálculos de precios sean consistentes
   * @param {Object} tourData - Datos del tour
   * @param {Object} pricing - Resultado del cálculo de precios
   * @returns {Object} Resultado de la validación
   */
  static validatePricing(tourData, pricing) {
    const errors = [];
    const warnings = [];
    
    // Validar que los días efectivos sean correctos
    const expectedDays = this.calculateEffectiveDays(
      tourData.booking.tour.effective_start_date,
      tourData.booking.tour.effective_end_date
    );
    
    if (pricing.breakdown.effectiveDays !== expectedDays) {
      errors.push(`Días efectivos incorrectos: esperado ${expectedDays}, calculado ${pricing.breakdown.effectiveDays}`);
    }
    
    // Validar que el GPS se calcule correctamente
    if (pricing.breakdown.gpsPrice > 0) {
      const expectedGpsPrice = (pricing.breakdown.gpsPrice / pricing.breakdown.effectiveDays) * expectedDays;
      if (Math.abs(pricing.breakdown.gpsPrice - expectedGpsPrice) > 0.01) {
        warnings.push(`Precio de GPS puede estar mal calculado: ${pricing.breakdown.gpsPrice} vs ${expectedGpsPrice}`);
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings
    };
  }

  /**
   * Calcula los totales usando la lógica exacta del backend
   * @param {Object} tourData - Datos del tour con booking y tour
   * @returns {Object} total1 y total2 calculados
   */
  static calculateTotalsExact(tourData) {
    if (!tourData || !tourData.booking || !tourData.booking.tour) {
      return { total1: 0, total2: 0, breakdown: {} };
    }

    const { tour } = tourData.booking;
    const data = tourData.booking;
    const rental = tourData.rental_info;

    let gpsPrice = 0;
    let singlePrice = 0;
    let vipInsurancePrice = 0;
    let cancelInsurancePercentage = 0;
    let cancelInsuranceValue1 = 0;
    let cancelInsuranceValue2 = 0;
    let model1Price = 0;
    let model2Price = 0;
    let tourDays = 0;
    let tourTotal1 = 0;
    let tourPrice1 = 0;
    let tourPrice2 = 0;
    let tourTotal2 = 0;
    let passengerPrice = 0;
    let passengerRegularPrice = 0;
    const rewards = data.rewards || 0;

    // 1. Cálculo de días del tour
  /*   tourDays = moment(tour.effective_end_date).diff(
      moment(tour.effective_start_date), 'days',
    ); */
    tourDays = this.duration(rental.effective_start_date, rental.effective_end_date, true);
      

    // 2. Obtener precios desde el pricing object
    const pricing = tourData.pricing;
    if (!pricing || !pricing.breakdown) {
      return { total1: 0, total2: 0, breakdown: { tourDays } };
    }

    const breakdown = pricing.breakdown;

    // 3. Precios de modelos
    model1Price = breakdown.modelPrice || 0;
    model2Price = breakdown.modelPrice || 0; // Mismo precio para ambas opciones

    // 4. Precio GPS
    gpsPrice = breakdown.gpsPrice || 0;

    // 5. Precio single
    singlePrice = breakdown.singlePrice || 0;

    // 6. Precio pasajero
    passengerPrice = breakdown.passengerPrice || 0;
    passengerRegularPrice = breakdown.passengerPrice || 0;

    // 7. Precios base del tour
    tourPrice1 = breakdown.basePrice || 0;
    tourPrice2 = breakdown.basePrice || 0; // Mismo precio para ambas opciones

    // 8. Seguro VIP
    vipInsurancePrice = breakdown.vipInsurancePrice || 0;

    // 9. Seguro de cancelación
    cancelInsurancePercentage = breakdown.cancelInsurancePercentage || 0;

    // 10. Cálculo de valores de cancelación
    cancelInsuranceValue1 = data.r_cancel_insurance
      ? ((tourPrice1
        + gpsPrice * tourDays
        + passengerPrice
        + singlePrice
        + model1Price)
        * cancelInsurancePercentage)
      / 100
      : 0;

    tourTotal1 = cancelInsuranceValue1
      + (tourPrice1
        + gpsPrice * tourDays
        + vipInsurancePrice * tourDays
        + passengerPrice
        + singlePrice
        + model1Price)
      - rewards;

    cancelInsuranceValue2 = data.r_cancel_insurance
      ? ((tourPrice2
        + gpsPrice * tourDays
        + passengerPrice
        + singlePrice
        + model2Price)
        * cancelInsurancePercentage)
      / 100
      : 0;

    tourTotal2 = cancelInsuranceValue2
      + (tourPrice2
        + gpsPrice * tourDays
        + vipInsurancePrice * tourDays
        + passengerPrice
        + singlePrice
        + model2Price)
      - rewards;

    return {
      total1: parseFloat(tourTotal1.toFixed(2)),
      total2: parseFloat(tourTotal2.toFixed(2)),
      breakdown: {
        tourDays,
        model1Price,
        model2Price,
        gpsPrice,
        singlePrice,
        passengerPrice,
        passengerRegularPrice,
        tourPrice1,
        tourPrice2,
        vipInsurancePrice,
        cancelInsurancePercentage,
        cancelInsuranceValue1,
        cancelInsuranceValue2,
        rewards,
      },
    };
  }

  /**
   * Obtiene un precio por subtipo
   * @param {Array} prices - Lista de precios
   * @param {string} subType - Subtipo de precio
   * @returns {number} Precio
   */
  static getPriceBySubType(prices, subType) {
    const priceItem = head(filter(propEq("price_item_sub_type", subType), prices));
    return priceItem ? prop("price", priceItem) : 0;
  }

  /**
   * Obtiene un precio por tipo
   * @param {Array} prices - Lista de precios
   * @param {string} type - Tipo de precio
   * @returns {number} Precio
   */
  static getPriceByType(prices, type) {
    const priceItem = head(filter(propEq("price_item_type", type), prices));
    return priceItem ? prop("price", priceItem) : 0;
  }

  /**
   * Obtiene un precio por ID
   * @param {Array} prices - Lista de precios
   * @param {number} id - ID del precio
   * @returns {number} Precio
   */
  static getPriceById(prices, id) {
    const priceItem = head(filter(propEq("price_item_id", id), prices));
    return priceItem ? prop("price", priceItem) : 0;
  }

  /**
   * Obtiene un porcentaje por ID
   * @param {Array} prices - Lista de precios
   * @param {number} id - ID del precio
   * @returns {number} Porcentaje
   */
  static getPercentageById(prices, id) {
    const priceItem = head(filter(propEq("price_item_id", id), prices));
    return priceItem ? prop("percentage", priceItem) : 0;
  }

  /**
   * Valida si un vehículo está disponible para las fechas del tour
   * @param {Object} vehicle - Vehículo a validar
   * @param {string} tourStartDate - Fecha de inicio del tour
   * @param {string} tourEndDate - Fecha de fin del tour
   * @returns {boolean} Si está disponible
   */
  static isVehicleAvailable(vehicle, tourStartDate, tourEndDate) {
    if (!vehicle.date_2) {
      return true; // Vehículo sin restricciones de fecha
    }

    const vehicleEndDate = moment(vehicle.date_2);
    const tourStart = moment(tourStartDate);
    const tourEnd = moment(tourEndDate);

    // Verificar si hay conflicto de fechas
    return !(tourStart.isBefore(vehicleEndDate) && tourEnd.isAfter(vehicleEndDate));
  }

  /**
   * Obtiene el nombre del modelo de vehículo
   * @param {Array} models - Lista de modelos
   * @param {number} modelId - ID del modelo
   * @returns {string} Nombre del modelo
   */
  static getModelName(models, modelId) {
    const model = head(filter(propEq("id", modelId), models));
    return model ? prop("model_name", model) : "Unknown Model";
  }
}

export default TourPricingService; 


// WEBPACK FOOTER //
// ./src/services/tourPricingService.js